import { MapContainer, TileLayer, GeoJSON, useMap, LayersControl } from 'react-leaflet';
import { MapUpdater } from './MapUpdater';
import { useEffect } from 'react';
import { getOrthophoto } from '../services/api';
import parseGeoraster from 'georaster';
import GeoRasterLayer from 'georaster-layer-for-leaflet';

const RasterMap: React.FC<{ buildingId?: string }> = ({ buildingId }) => {
  const map = useMap();

  useEffect(() => {
    if (!buildingId) {
      return;
    }
    const loadRaster = async (buildingId: string) => {
      const orthophotoUrl = await getOrthophoto(buildingId);
      fetch(orthophotoUrl)
        .then(response => response.arrayBuffer())
        .then(parseGeoraster)
        .then(georaster => {
          //console.log("georaster:", georaster);
          // drop NIR/alpha id present
          if (georaster.numberOfRasters == 4) {
            georaster.values = georaster.values.slice(0, 3);
          }
          const layer = new GeoRasterLayer({
            georaster: georaster,
            opacity: 1.0,
            resolution: 300
          });
          layer.addTo(map);
        })
    };
    loadRaster(buildingId);
  }, [buildingId, map]);

  return null;
};

type ResultMapProps = {
  buildingId?: string;
  panelGeoJSON: GeoJSON.FeatureCollection | null;
  obstructionGeoJSON: GeoJSON.FeatureCollection | null;
  surfacesGeoJSON: GeoJSON.FeatureCollection | null;
  existingPvGeoJSON: GeoJSON.FeatureCollection | null;
  mapBounds?: L.LatLngBounds;
  handleSurfaceClick: (surfaceId: string) => void;
  handleSurfaceHover?: (surfaceId: string) => void;
  selectedSurfaces: any;
};

const ResultMap: React.FC<ResultMapProps> = ({ buildingId, panelGeoJSON, obstructionGeoJSON, surfacesGeoJSON, existingPvGeoJSON, mapBounds, handleSurfaceClick, handleSurfaceHover, selectedSurfaces }) => {
  // const [selectedSurfaces, setSelectedSurfaces] = useState<string[]>([]);

  const handleObstructionToggle = (obstruction: GeoJSON.Feature) => {
    console.log('obstruction clicked', obstruction);
  };

  return (
    <MapContainer
      center={[51.1657, 10.4515]}
      zoom={18}
      scrollWheelZoom={true}
      maxZoom={22}
      style={{ height: '100%', width: '100%' }}
    >
      <MapUpdater bounds={mapBounds} />
      <TileLayer
        url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        maxZoom={23}
      />
      <RasterMap buildingId={buildingId} />

      <LayersControl position="topright">
        <LayersControl.Overlay name="Dachflächen" checked>
          {surfacesGeoJSON && (
            <GeoJSON
              data={surfacesGeoJSON}
              style={(feature) => ({
                color: selectedSurfaces.includes(feature?.properties.surface_gmlid) ? '#00FF00' : '#0000FF',
                weight: 1,
                opacity: selectedSurfaces.includes(feature?.properties.surface_gmlid) ? 0.7 : 0.4,
                fillOpacity: 0
              })}
              onEachFeature={(feature, layer) => {
                layer.on({
                  click: () => {
                    handleSurfaceClick(feature.properties.surface_gmlid);
                  },
                  mouseover: () => {
                    handleSurfaceHover?.(feature.properties.surface_gmlid);
                    layer.bindPopup("Dachfläche "+ (feature.properties.id+1), { className: "surfacepopup" }).openPopup();
                  }
                });
              }}
            />
          )}
        </LayersControl.Overlay>

        <LayersControl.Overlay name="Bestehende PV" checked>
          {existingPvGeoJSON && (
          <GeoJSON
            data={existingPvGeoJSON}
            style={{
              color: '#00FFAA',
              weight: 1,
              opacity: 1,
              fillOpacity: 0.4
            }}
            onEachFeature={(feature, layer) => {
                layer.on({
                  mouseover: () => {
                    handleSurfaceHover?.(feature.properties.surface_gmlid);
                    layer.bindPopup(`Bestands-PV ${Number(feature.properties.id)+1}<br>Fläche: ${feature.properties.area.toLocaleString()} m²<br>Leistung: ${feature.properties.estimated_power_range_kwp[0].toLocaleString(undefined, {maximumFractionDigits: 2})}-${feature.properties.estimated_power_range_kwp[1].toLocaleString(undefined, {maximumFractionDigits: 2})} kWp<br>Ertrag: ${feature.properties.estimated_yield_range_kwh_per_a[0].toLocaleString(undefined, {maximumFractionDigits: 0})}-${feature.properties.estimated_yield_range_kwh_per_a[1].toLocaleString(undefined, {maximumFractionDigits: 0})} kWh/a`, { className: "existingpvpopup" }).openPopup();
                  }
                });
              }}
            />
        )}
        </LayersControl.Overlay>

        <LayersControl.Overlay name="Störflächen" checked>
          {obstructionGeoJSON && (
          <GeoJSON
            data={obstructionGeoJSON}
            style={{
              color: '#FF0000',
              weight: 1,
              opacity: 1,
              fillOpacity: 0.4
            }}
            // onEachFeature={(feature, layer) => {
            //   layer.on({
            //     click: () => {
            //       handleObstructionToggle(feature);
            //     }
            //   });
            // }}
            />
        )}
        </LayersControl.Overlay>

        <LayersControl.Overlay name="Solarmodule" checked>
          {panelGeoJSON && (
          <GeoJSON
            data={panelGeoJSON}
            style={{
              color: '#0005AF',
              weight: 1,
              opacity: 0.7,
              fillOpacity: 0.4
            }}
            // onEachFeature={(feature, layer) => {
            //   layer.on({
            //     click: () => {
            //       console.log('panel clicked', feature);
            //     }
            //   });
            // }}
          />
        )}
        </LayersControl.Overlay>
      </LayersControl>
    </MapContainer>
  );
};

export default ResultMap;